/**
 * Inline "house number missing" error on the Checkout block's street fields.
 *
 * Puts the message under the street field through the block's own validation
 * store, which also keeps "Place order" from submitting and scrolls to the
 * field. A soft check, in the form only: the server accepts any address, so
 * orders from the Store API or an express wallet are never refused over it
 * (see inc/address-validation.php).
 *
 * The check runs when the shopper leaves the field and when "Place order" is
 * clicked — not while typing, so nobody is told off half-way through a word.
 * Part of main.js on purpose: it must hold with address suggestions switched off.
 *
 * Messages come from `niamitoHouseNumber`, localised on the checkout only.
 */

import { hasHouseNumber } from './houseNumberRule';

type AddressType = 'shipping' | 'billing';

const messages = (window as any).niamitoHouseNumber as Record<AddressType, string> | undefined;
const VALIDATION_STORE = 'wc/store/validation';

// The address-search controls next to the street field (addressAutocomplete.ts).
const STILL_CHOOSING = [
  '.wc-block-components-address-autocomplete-icon',
  '.wc-block-components-address-autocomplete-suggestions',
  '.wc-block-components-address-autocomplete-suggestion',
  '.woocommerce-address-suggestions',
  '.address-search-icon',
].join(', ');

function store() {
  const data = (window as any).wp?.data;
  if (!data?.select || !data?.dispatch) {
    return null;
  }
  try {
    return { select: data.select(VALIDATION_STORE), dispatch: data.dispatch(VALIDATION_STORE) };
  } catch {
    return null;
  }
}

function check(type: AddressType, show: boolean): void {
  const validation = store();
  if (!validation || !messages) {
    return;
  }

  const id = `${type}_address_1`;
  const message = messages[type];
  const input = document.getElementById(`${type}-address_1`) as HTMLInputElement | null;
  const value = input ? input.value.trim() : '';
  const current = validation.select.getValidationError(id);

  // A hidden form (billing folded into shipping) or an empty field — the block
  // reports "required" itself — or a valid one: drop only our own error.
  if (!input || !value || hasHouseNumber(value)) {
    if (current && current.message === message) {
      validation.dispatch.clearValidationError(id);
    }
    return;
  }

  // Never replace one of the block's own errors for this field.
  if (current && current.message !== message) {
    return;
  }

  validation.dispatch.setValidationErrors({ [id]: { message, hidden: !show } });
}

function typeOf(target: EventTarget | null): AddressType | null {
  if (!(target instanceof HTMLInputElement)) {
    return null;
  }
  if (target.id === 'shipping-address_1') {
    return 'shipping';
  }
  if (target.id === 'billing-address_1') {
    return 'billing';
  }
  return null;
}

if (messages) {
  // After the block's own blur validation, which clears errors on a field it
  // considers valid.
  document.addEventListener('focusout', (event) => {
    const type = typeOf(event.target);
    // Not when focus only moves on to the search button or a suggestion: the
    // shopper is still choosing the address, and a pick may add the number.
    const next = event.relatedTarget as Element | null;
    if (type && !next?.closest?.(STILL_CHOOSING)) {
      window.setTimeout(() => check(type, true), 0);
    }
  });

  // Capture phase: settled before the block decides whether it may submit.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target as Element | null;
      if (target?.closest?.('.wc-block-components-checkout-place-order-button')) {
        check('shipping', true);
        check('billing', true);
      }
    },
    true,
  );
}
